import { useContext } from 'react';

import { useAppSelector, useAppDispatch } from 'hooks';

import { Text, OrderedList, ListItem, Box } from '@chakra-ui/react';

import Card from 'components/card/Card';

import TextFieldInput from 'components/fields/home/TextFieldInput';

import { ApplicationFormContext } from 'contexts/ApplicationFormContext';

import capitalizeFirstLetterInAllWords from 'helpers/capitalizeFirstLetterInAllWords';

import {
  addOrUpdateRemark,
  setNotesToUnderwriter,
} from 'reducers/homePersonalFormReducer';

import { SECTIONS } from 'views/home_form/constants';

import getValidationMessage from 'helpers/getValidationMessage';

import Message from 'components/fields/home/Message';

import { Validation561Resolver } from 'components/fields/auto/resolvers';

import { FILE_TYPE } from 'variables/constants';

import type { APIRemark } from 'types/api.types';

const Remarks = () => {
  const dispatch = useAppDispatch();

  const { validations } = useContext(ApplicationFormContext) as {
    validations: Validation[];
  };

  const messages503 = validations.find((v) => v.validation_id === '503')
    ?.validation_outcome_data?.messages_by_dwelling;

  const remarksWithText = useAppSelector((state) =>
    state.homePersonalForm.remarks.filter(
      (r) => r.text && r.text.trim().length > 0,
    ),
  );

  const additionalRemark = useAppSelector((state) =>
    state.homePersonalForm.remarks.find(
      (r) => r.validation_key === 'additional',
    ),
  );
  const notesToUnderwriter = useAppSelector(
    (state) => state.homePersonalForm.data?.notes_to_underwriter || '',
  );

  const getRemarkText = (remark: APIRemark) => {
    const validation = validations.find(
      (v) => Number(v.validation_id) === Number(remark.validation_key),
    );
    const validation_name =
      validation?.validation_name ||
      capitalizeFirstLetterInAllWords(
        remark.validation_key.replaceAll('_', ' '),
      );

    return `${validation_name} Remarks - ${remark.text}`;
  };

  const onGlobalRemarksChange = (event: any) => {
    const { name, value } = event.target;

    dispatch(
      addOrUpdateRemark({
        validation_key: name,
        item_key: 'global',
        data: {
          text: value,
        },
      }),
    );
  };

  const onNotesToUnderwriterChange = (event: any) => {
    dispatch(setNotesToUnderwriter(event.target.value));
  };

  return (
    <Card mb="20px" id="sectionRemarks">
      <Text fontWeight="bold" fontSize="xl" mb="20px">
        Remarks
      </Text>

      {!messages503 && getValidationMessage(validations, '503') && (
        <Box mb="20px">
          <Message validationId="503" section={SECTIONS.REMARKS} />
        </Box>
      )}

      {getValidationMessage(validations, '524') && (
        <Box mb="20px">
          <Message
            validationId="524"
            hideRemarks
            section={SECTIONS.REMARKS}
            file={FILE_TYPE.TYPE_524}
          />
        </Box>
      )}

      {getValidationMessage(validations, '549') && (
        <Box mb="20px">
          <Message validationId="549" section={SECTIONS.REMARKS} />
        </Box>
      )}

      {getValidationMessage(validations, '551') && (
        <Box mb="20px">
          <Message
            validationId="551"
            hideRemarks
            section={SECTIONS.REMARKS}
            file={FILE_TYPE.TYPE_551}
          />
        </Box>
      )}

      {getValidationMessage(validations, '552') && (
        <Box mb="20px">
          <Message
            validationId="552"
            section={SECTIONS.REMARKS}
            file={FILE_TYPE.TYPE_MARKET_APPROVAL_PROPERTY}
          />
        </Box>
      )}

      {getValidationMessage(validations, '553') && (
        <Box mb="20px">
          <Message validationId={'553'} section={SECTIONS.REMARKS} />
        </Box>
      )}

      {getValidationMessage(validations, '558') && (
        <Box mb="20px">
          <Message validationId={'558'} section={SECTIONS.REMARKS} />
        </Box>
      )}

      {getValidationMessage(validations, '555') && (
        <Box mb="20px">
          <Message
            validationId="555"
            section={SECTIONS.REMARKS}
            file={FILE_TYPE.TYPE_MARKET_APPROVAL_PROPERTY}
          />
        </Box>
      )}

      {getValidationMessage(validations, '550') && (
        <Box mb="20px">
          <Message
            validationId="550"
            section={SECTIONS.REMARKS}
            file={FILE_TYPE.TYPE_MARKET_APPROVAL_PROPERTY}
          />
        </Box>
      )}

      {getValidationMessage(validations, '560') && (
        <Box mb="20px">
          <Message
            validationId="560"
            hideRemarks
            section={SECTIONS.REMARKS}
            file={FILE_TYPE.TYPE_560}
          />
        </Box>
      )}

      {getValidationMessage(validations, '561') && (
        <Box mb="20px">
          <Validation561Resolver section={SECTIONS.REMARKS} />
        </Box>
      )}

      {!remarksWithText.length && <div>Empty...</div>}

      {remarksWithText.length > 0 && (
        <OrderedList spacing="1">
          {remarksWithText.map((remark) => {
            return (
              <ListItem key={`${remark.validation_key}-${remark.item_key}`}>
                {getRemarkText(remark)}
              </ListItem>
            );
          })}
        </OrderedList>
      )}

      <Box mt="20px">
        <TextFieldInput
          section={SECTIONS.REMARKS}
          label="Additional Remarks"
          value={additionalRemark?.text || ''}
          onChange={onGlobalRemarksChange}
          name="additional"
          maxH="auto"
        />
      </Box>

      <Box mt="20px">
        <TextFieldInput
          section={SECTIONS.REMARKS}
          label="Notes for BIG Underwriting"
          value={notesToUnderwriter}
          onChange={onNotesToUnderwriterChange}
          name="notes_to_underwriter"
          maxH="auto"
        />
      </Box>
    </Card>
  );
};

export default Remarks;
